import api from './api';
import { TierInfo, Subscription, ResourceLimit, FeatureAccess, SubscriptionTier } from '../types/payment';

export interface GetSubscriptionTiersResponse {
  success: boolean;
  data: {
    tiers: TierInfo[];
    stripeConfigured: boolean;
  };
}

export interface GetCurrentSubscriptionResponse {
  success: boolean;
  data: Subscription;
}

export interface CreateCheckoutResponse {
  success: boolean;
  data: {
    sessionId: string;
    url: string;
  };
}

export interface CreateBillingPortalResponse {
  success: boolean;
  data: {
    url: string;
  };
}

export interface CancelSubscriptionResponse {
  success: boolean;
  data: {
    message: string;
    cancelAt: Date;
  };
}

export interface ReactivateSubscriptionResponse {
  success: boolean;
  data: {
    message: string;
  };
}

export interface CheckResourceLimitResponse {
  success: boolean;
  data: ResourceLimit;
}

export interface CheckFeatureAccessResponse {
  success: boolean;
  data: FeatureAccess;
}

/**
 * Get all available subscription tiers
 */
export const getSubscriptionTiers = async (): Promise<{
  tiers: TierInfo[];
  stripeConfigured: boolean;
}> => {
  const response = await api.get<GetSubscriptionTiersResponse>('/api/payments/tiers');
  return response.data.data;
};

/**
 * Get current user's subscription
 */
export const getCurrentSubscription = async (): Promise<Subscription> => {
  const response = await api.get<GetCurrentSubscriptionResponse>('/api/payments/subscription');
  return response.data.data;
};

/**
 * Create a checkout session for upgrading/subscribing
 */
export const createCheckout = async (tier: SubscriptionTier): Promise<{ sessionId: string; url: string }> => {
  const response = await api.post<CreateCheckoutResponse>('/api/payments/checkout', { tier });
  return response.data.data;
};

/**
 * Create a billing portal session
 */
export const createBillingPortal = async (): Promise<{ url: string }> => {
  const response = await api.post<CreateBillingPortalResponse>('/api/payments/billing-portal');
  return response.data.data;
};

/**
 * Cancel subscription (will cancel at end of billing period)
 */
export const cancelSubscription = async (): Promise<{ message: string; cancelAt: Date }> => {
  const response = await api.post<CancelSubscriptionResponse>('/api/payments/subscription/cancel');
  return response.data.data;
};

/**
 * Reactivate a canceled subscription
 */
export const reactivateSubscription = async (): Promise<{ message: string }> => {
  const response = await api.post<ReactivateSubscriptionResponse>('/api/payments/subscription/reactivate');
  return response.data.data;
};

/**
 * Check resource limit for Mundus, Agents, or Metrics
 */
export const checkResourceLimit = async (
  resourceType: 'mundus' | 'agents' | 'metrics'
): Promise<ResourceLimit> => {
  const response = await api.get<CheckResourceLimitResponse>(`/api/payments/limits/${resourceType}`);
  return response.data.data;
};

/**
 * Check if user has access to a specific feature
 */
export const checkFeatureAccess = async (feature: string): Promise<boolean> => {
  const response = await api.get<CheckFeatureAccessResponse>(`/api/payments/features/${feature}`);
  return response.data.data.hasAccess;
};
